import axios from "axios";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { MagnifyingGlassCircleIcon, PhoneIcon, DocumentTextIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import Button from "../common/Button";

// Shown on direct navigation to /investigations without a seat. Not a 404:
// this is a sales surface.
const InvestigationUpsell: React.FC = () => {
  const { user, addons } = useAuth();
  const [loading, setLoading] = useState(false);
  const ent = addons.investigations;
  const isAgency = Boolean(user?.agency_id);
  const monthly = ent?.pricePerSeat ?? 180;
  const annual = ent?.pricePerSeatAnnual ?? 1800;
  const [interval, setInterval] = useState<"month" | "year">(ent?.annualAvailable ? "year" : "month");

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post("/api/subscription/addon-checkout", { addon: "investigations", interval });
      if (data.url) window.location.href = data.url;
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Could not start checkout");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto mt-8">
        <div className="flex items-center gap-3 mb-2">
          <MagnifyingGlassCircleIcon className="size-9 text-blue-600 dark:text-blue-300" />
          <h1 className="text-2xl font-bold text-base-content">Investigations</h1>
        </div>
        <p className="text-base-content/70 mb-6">
          Upload every jail call on a case. Each one is transcribed, translated when needed, and read in full by AI that ranks
          which calls to listen to and why, flags threats, witness tampering, three-way calls, money movement, and coded
          language with a play button on the exact moment, and answers questions about the whole investigation with citations.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { icon: PhoneIcon, title: "Ranked calls", body: "Fifty calls in, the six worth an hour out. Nothing is hidden; low scores let you skip with confidence." },
            { icon: DocumentTextIcon, title: "Every file type", body: "PDFs (with OCR), bank spreadsheets, photos, and call audio in one place, all searchable." },
            { icon: ChatBubbleLeftRightIcon, title: "Ask the case", body: "\"When did he mention the gun?\" gets an answer with a timestamp you can click." },
          ].map((f) => (
            <div key={f.title} className="rounded-xl border border-base-300 bg-base-100 p-4">
              <f.icon className="size-6 text-blue-600 dark:text-blue-300 mb-2" />
              <p className="font-semibold text-base-content">{f.title}</p>
              <p className="text-sm text-base-content/70">{f.body}</p>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-base-300 bg-base-200 p-5">
          {isAgency ? (
            <>
              <p className="font-medium text-base-content">
                {ent?.reason === "no_grant" && (ent.seatsPurchased || 0) > 0
                  ? `Your agency has ${ent.seatsPurchased} Investigations seat${ent.seatsPurchased === 1 ? "" : "s"} (${ent.seatsUsed || 0} assigned). Ask your agency administrator to assign one to you.`
                  : ent?.reason === "expired"
                    ? "Your agency's Investigations subscription has lapsed. Ask your agency administrator to renew it."
                    : "Investigations is a per-investigator add-on. Ask your agency administrator to purchase seats from the agency dashboard."}
              </p>
              <p className="text-sm text-base-content/60 mt-1">${monthly} per investigator per month, or ${annual} per year (${Math.round(annual / 12)}/mo).</p>
              <p className="text-sm text-base-content/60 mt-1">Every seat adds 50 hours of audio a month to your department's shared pool (pooled across the whole year on an annual plan), so one heavy case never blocks the rest of the team.</p>
            </>
          ) : (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-medium text-base-content">Add Investigations to your account</p>
                <p className="text-sm text-base-content/60">Includes 50 hours of audio a month, or 600 hours to use across the year on the annual plan. Cancel any time from billing.</p>
                {ent?.annualAvailable && (
                  <div className="mt-2 flex gap-2">
                    {(["year", "month"] as const).map((i) => (
                      <button
                        key={i}
                        onClick={() => setInterval(i)}
                        className={`px-3 py-1.5 rounded-lg border text-sm ${interval === i ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "border-base-300 hover:bg-base-100"}`}
                      >
                        {i === "year" ? `$${annual}/year · $${Math.round(annual / 12)}/mo` : `$${monthly}/month`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="primary" onClick={buy} loading={loading}>
                Add Investigations
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestigationUpsell;
